import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { missionErrorMessage } from "../lib/missions";
import { StatePill } from "../components/CollaboratorBits";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, ChevronRight, ClipboardCheck, Loader2, Plus, RefreshCw } from "lucide-react";


// Procédures et checklists, for those who write them (procedures:manage). The route is gated on that key.
export default function Procedures() {
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "loading", items: [], error: "" });
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/procedures");
      setState({ phase: "ready", items: data.items, error: "" });
    } catch (e) {
      setState({ phase: "error", items: [], error: missionErrorMessage(e, "Impossible de charger les procédures.") });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/procedures", { label: label.trim(), description: description.trim() || null });
      toast.success("Procédure créée : complétez son premier brouillon");
      navigate(`/missions/procedures/${data.id}`);
    } catch (e) {
      toast.error(missionErrorMessage(e, "Impossible de créer la procédure."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="procedures-page">
      <Link to="/missions" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ArrowLeft size={14} /> Retour aux missions</Link>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl text-slate-900">Procédures</h1>
          <p className="text-sm text-slate-500">Les checklists suivies sur place. Une version publiée ne change plus : on en publie une nouvelle.</p>
        </div>
        <Button onClick={() => { setLabel(""); setDescription(""); setOpen(true); }} className={`h-9 shrink-0`} data-testid="procedures-new">
          <Plus size={16} className="mr-1.5" />Nouvelle procédure
        </Button>
      </div>

      {state.phase === "loading" && <div className="space-y-2"><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /></div>}
      {state.phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{state.error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {state.phase === "ready" && state.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="procedures-empty">
          <ClipboardCheck className="mx-auto text-slate-300" size={26} />
          <h3 className="font-medium text-slate-900">Aucune procédure pour le moment</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">Décrivez par exemple l'installation de la caisse ou le contrôle du réseau, étape par étape, puis rattachez la procédure à un type de mission.</p>
        </Card>
      )}
      {state.phase === "ready" && state.items.length > 0 && (
        <Card className="divide-y divide-slate-100 overflow-hidden" data-testid="procedures-list">
          {state.items.map((p) => (
            <Link key={p.id} to={`/missions/procedures/${p.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`procedure-row-${p.code}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-900 break-words">{p.label}</span>
                  <StatePill active={p.active} activeLabel="Active" inactiveLabel="Désactivée" />
                </div>
                <div className="text-xs text-slate-500">
                  {p.current_version ? `Version ${p.current_version.number} publiée` : "Jamais publiée"}
                  {p.draft_version ? ` · brouillon v${p.draft_version.number} en cours` : ""}
                  {p.description ? ` · ${p.description}` : ""}
                </div>
              </div>
              <ChevronRight size={16} className="text-slate-300 shrink-0" />
            </Link>
          ))}
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="procedure-create-dialog">
          <DialogHeader>
            <DialogTitle>Nouvelle procédure</DialogTitle>
            <DialogDescription>Vous la composerez ensuite, puis la publierez quand elle sera prête.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label htmlFor="procedure-label">Nom *</Label>
              <Input id="procedure-label" maxLength={120} className="text-base md:text-sm" placeholder="Ex. Installation de la caisse" value={label}
                onChange={(e) => setLabel(e.target.value)} data-testid="procedure-create-label" /></div>
            <div className="space-y-1.5"><Label htmlFor="procedure-description">Description</Label>
              <Textarea id="procedure-description" rows={2} maxLength={2000} className="text-base md:text-sm" value={description} onChange={(e) => setDescription(e.target.value)} /></div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
            <Button onClick={create} disabled={busy || !label.trim()}  data-testid="procedure-create-confirm">
              {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
